import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';

import {ErrorBoundary} from '@sentry/react';
import isString from 'lodash.isstring';
import {ChunkedDownloadTool} from 'src/components/chuncked-download-tool';
import {GroupSuggest} from 'src/components/group-suggest';
import {DateRangeInput} from 'src/components/input';
import {ProfileMain} from 'src/components/profile-tab-structure';
import {RequiredMark} from 'src/components/required-mark';
import {WidthItem} from 'src/components/width-item';
import {NO_RESULTS_MESSAGE, STACKOVERFLOW_MESSAGE} from 'src/constants';
import {useBackgroundTaskManager} from 'src/contexts/background-task-context';
import {ValidatedControl} from 'src/contexts/validation-context';
import {ValidationError} from 'src/contexts/validation-context/validation-context';
import {useDateTime} from 'src/hooks/date-time/useDateTime';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {ErrorPage} from 'src/modules/error-page/error-page.components';
import {selectAccountLicense} from 'src/modules/profile/account/tabs/license-and-features/services/account-license-features-slice';
import {getUserListIds} from 'src/openapi/admin/apis/UserListReportApi';
import {downloadUserPlaceReport} from 'src/openapi/admin/apis/UserPlaceReportApi';
import {UserStateCriteria as UserStates} from 'src/openapi/admin/models/UserStateCriteria';
import {useAppDispatch} from 'src/store/hooks';
import {getError} from 'src/utils/get-error';

import Button from '@pb/kit-component-button';
import {FormSection} from '@pb/kit-component-form-section';
import {Group, Item} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import {RadioButton, RadioButtons} from '@pb/kit-component-radio-button';
import Typography from '@pb/kit-component-typography';

import {UserStateSuggest} from '../../components/user-state-suggest';
import {UsersSuggest} from '../../components/users-suggest';
import {isValidDateRange, validateEndDate} from '../../components/utils/dateValidationUtils';
import {REPORTS_SUB_TABS} from '../../constants';
import {ReportsTabs} from '../../report-tabs';

import {
    CHUNK_SIZE,
    DATE_RANGE_NAME,
    DATE_RANGE_OPTIONS,
    FILE_NAME,
    GET_USERS_TASK_NAME,
    GET_USERS_TASK_PROGRESS_LABEL,
    REPORT_NAME,
    SELECT_NAME,
    SELECT_OPTIONS,
    STATIC_FILE_HEADERS,
    TASK_NAME,
} from './constants';

import type {UserSearchResult} from 'src/openapi/admin/models/UserSearchResult';
import type {UserStateCriteria} from 'src/openapi/admin/models/UserStateCriteria';


export const AssociatedBrowsersReport = () => {
    const queryParams = useParams<{accountId: string, licenseId: string}>();
    const accountId = queryParams.accountId ? +queryParams.accountId : -1;
    const licenseId = queryParams.licenseId ? +queryParams.licenseId : -1;
    const apiDispatch = useAppDispatch();
    const {accountReportRun} = useEventTracking();

    const license = useSelector(selectAccountLicense);
    const createBackgroundTask = useBackgroundTaskManager();
    const {getCalendarMonthPeriod} = useDateTime();

    const [dateRange, setDateRange] = useState(DATE_RANGE_OPTIONS.CUSTOM.value);
    const [customDateRange, setCustomDateRange] = useState<{startDate?: string, endDate?: string}>({});
    const [userStates, selectUserStates] = useState<UserStateCriteria[]>([UserStates.LICENSED]);

    const [selectMode, setSelectMode] = useState(SELECT_OPTIONS.GROUPS.value);
    const [selectedUsers, setSelectedUsers] = useState<(UserSearchResult | string)[]>([]);
    const [selectedGroup, setSelectedGroup] = useState<number | null>(null);

    useEffect(() => {
        if (license) {
            setCustomDateRange({
                startDate: license.startDate,
                endDate: license.endDate,
            });
        }
    }, [license]);

    const changeMode = useCallback((mode: string) => {
        if (mode === DATE_RANGE_OPTIONS.CURRENT_LICENSE.value && license) {
            setCustomDateRange({
                startDate: license.startDate,
                endDate: license.endDate,
            });
        } else if (mode === DATE_RANGE_OPTIONS.LAST_MONTH.value) {
            setCustomDateRange(getCalendarMonthPeriod());
        }

        setDateRange(mode);
    }, [license, getCalendarMonthPeriod]);

    const changeSelectionMode = useCallback((mode: string) => {
        setSelectMode(mode);
        if (mode === SELECT_OPTIONS.GROUPS.value) {
            setSelectedUsers([]);
            selectUserStates([UserStates.LICENSED]);
        } else {
            setSelectedGroup(null);
        }
    }, []);

    const changeStartDate = useCallback((startDate: string) => {
        setCustomDateRange(prevState => {
            if (prevState.startDate !== startDate) {
                setDateRange(DATE_RANGE_OPTIONS.CUSTOM.value);
            }

            return {...prevState, startDate};
        });
    }, []);

    const changeEndDate = useCallback((endDate: string) => {
        setCustomDateRange(prevState => {
            if (prevState.startDate !== endDate) {
                setDateRange(DATE_RANGE_OPTIONS.CUSTOM.value);
            }

            return {...prevState, endDate};
        });
    }, []);

    const runReport = useCallback(async (handler: (ids: number[]) => void) => {
        if (selectedUsers.length) {
            const validUsers = selectedUsers.filter(value => !isString(value)) as UserSearchResult[];

            handler(validUsers.map(user => user.id));
        } else {
            const getUsersTask = createBackgroundTask(
                GET_USERS_TASK_NAME,
                <b>{GET_USERS_TASK_PROGRESS_LABEL}</b>,
                {
                    maxStack: 1,
                    stackOverflowMessage: STACKOVERFLOW_MESSAGE,
                },
            );

            if (!getUsersTask) {
                return;
            }

            const userIdsResponse = await apiDispatch(getUserListIds({
                accountId,
                states: userStates,
                groupId: selectedGroup || undefined,
            }));

            if (userIdsResponse.payload.success && userIdsResponse.payload.data.length) {
                getUsersTask.completeSuccess();
                handler(userIdsResponse.payload.data);
            } else if (userIdsResponse.payload.success) {
                getUsersTask.completeFailure(<b>{NO_RESULTS_MESSAGE}</b>);
                accountReportRun({reportType: REPORT_NAME, success: false});
            } else {
                accountReportRun({reportType: REPORT_NAME, success: false});
                getUsersTask.completeFailure(
                    <b>
                        {getError({
                            error: userIdsResponse.payload.data,
                            separator: '',
                        })}
                    </b>,
                );
            }
        }
    }, [accountId, selectedGroup, selectedUsers, userStates, accountReportRun, apiDispatch, createBackgroundTask]);


    const isRunDisabled = useMemo(() => {
        const validUsers = selectedUsers.filter(value => !isString(value)) as UserSearchResult[];

        if (selectedUsers.length) {
            return !validUsers.length;
        }

        return !(userStates.length && isValidDateRange(customDateRange.endDate, customDateRange.startDate));
    }, [customDateRange, selectedUsers, userStates]);


    const changeSelectedUsers = useCallback((users: (UserSearchResult | string)[]) => {
        setSelectedUsers(users);

        if (users.length) {
            selectUserStates([]);
        } else {
            selectUserStates([UserStates.LICENSED]);
        }
    }, []);

    const validateEndDateWrapper = (): ValidationError | null => {
        return validateEndDate(customDateRange.endDate, customDateRange.startDate, 'BROWSER_REPORT_END_DATE');
    };

    return (
        <ErrorBoundary fallback={<ErrorPage subtitle="Please try again later." title="Something went wrong."/>}>
            <ReportsTabs
                accountId={accountId}
                licenseId={licenseId}
                activeSubTab={REPORTS_SUB_TABS.ASSOCIATED_BROWSERS.name}
            />
            <ProfileMain header={(
                <Item grow={1}>
                    <Group justify="space-between">
                        <Typography
                            fontWeight="semi-bold"
                            variant="heading-level-3">
                            {REPORTS_SUB_TABS.ASSOCIATED_BROWSERS.caption}
                        </Typography>
                        <ChunkedDownloadTool
                            taskName={TASK_NAME}
                            fileName={FILE_NAME}
                            fileHeaders={STATIC_FILE_HEADERS}
                            getData={async ids => {
                                const chunkDataResponse = await apiDispatch(downloadUserPlaceReport({
                                    userPlaceRequest: {
                                        ids,
                                        startDate: customDateRange.startDate || '',
                                        endDate: customDateRange.endDate || '',
                                    },
                                }));

                                if (chunkDataResponse.payload.success) {
                                    const responseData = chunkDataResponse.payload.data;
                                    const data = responseData.map(item => {
                                        return {
                                            ...item,
                                            registered: item.registered ? 'Yes' : 'No',
                                            deactivated: item.deactivated ? 'Yes' : 'No',
                                        };
                                    });

                                    return Promise.resolve({
                                        type: '', payload: {
                                            success: true,
                                            error: false,
                                            data,
                                        },
                                    });
                                } else {
                                    return Promise.resolve({type: '', payload: chunkDataResponse.payload});
                                }
                            }}
                            onSuccess={() => accountReportRun({reportType: REPORT_NAME, success: true})}
                            onError={() => accountReportRun({reportType: REPORT_NAME, success: false})}
                            chunkSize={CHUNK_SIZE}>
                            {handler => (
                                <Button
                                    onClick={() => runReport(handler)}
                                    disabled={isRunDisabled}
                                    variant="Type.PRIMARY">
                                    Run Report
                                </Button>
                            )}
                        </ChunkedDownloadTool>
                    </Group>
                </Item>
            )}>
                <FormSection headline="Configuration">
                    <Group direction="v">
                        <Group direction="v" gap="xs">
                            <Label><RequiredMark label="Date Range"/></Label>
                            <RadioButtons
                                name={DATE_RANGE_NAME}
                                value={dateRange}
                                onChange={changeMode}>
                                <Group gap="s">
                                    <RadioButton {...DATE_RANGE_OPTIONS.CUSTOM}/>
                                    <RadioButton {...DATE_RANGE_OPTIONS.LAST_MONTH}/>
                                    <RadioButton {...DATE_RANGE_OPTIONS.CURRENT_LICENSE}/>
                                </Group>
                            </RadioButtons>
                            <DateRangeInput
                                id="associated-browsers-start-date-picker"
                                label={<>Starts</>}
                                width={300}
                                clearable={true}
                                startDate={customDateRange.startDate}
                                endDate={customDateRange.endDate}
                                selectRangeType="start"
                                onChange={changeStartDate}
                            />
                            <ValidatedControl
                                name="BROWSER_REPORT_END_DATE"
                                validate={validateEndDateWrapper}>
                                {(invalid: boolean) => (
                                    <DateRangeInput
                                        id="associated-browsers-end-date-picker"
                                        label={<>Ends&nbsp;&nbsp;</>}
                                        width={300}
                                        invalid={invalid}
                                        clearable={true}
                                        startDate={customDateRange.startDate}
                                        endDate={customDateRange.endDate}
                                        selectRangeType="end"
                                        onChange={changeEndDate}
                                    />
                                )}
                            </ValidatedControl>
                        </Group>
                        <Group gap="l">
                            <WidthItem width={300}>
                                <Label>User States</Label>
                                <UserStateSuggest
                                    disabled={selectedUsers.length > 0}
                                    userStates={userStates}
                                    onSelect={selectUserStates}
                                />
                            </WidthItem>
                        </Group>
                        <Group direction="v" gap="xs">
                            <Label>Select</Label>
                            <RadioButtons
                                name={SELECT_NAME}
                                value={selectMode}
                                onChange={changeSelectionMode}>
                                <Group gap="l">
                                    <RadioButton {...SELECT_OPTIONS.USERS}/>
                                    <RadioButton {...SELECT_OPTIONS.GROUPS}/>
                                </Group>
                            </RadioButtons>
                            {selectMode === SELECT_OPTIONS.USERS.value && (
                                <WidthItem width={624}>
                                    <UsersSuggest
                                        id="associated-browsers-users-suggest"
                                        accountId={accountId}
                                        selectedUsers={selectedUsers}
                                        setSelectedUsers={changeSelectedUsers}
                                        placeholder="All Users"
                                    />
                                </WidthItem>
                            )}
                            {selectMode === SELECT_OPTIONS.GROUPS.value && (
                                <GroupSuggest
                                    id="associated-browsers-group-suggest"
                                    width={300}
                                    accountId={accountId}
                                    placeholder="All Groups"
                                    selectedGroupId={selectedGroup}
                                    onSelect={group => setSelectedGroup(group?.id || null)}
                                />
                            )}
                        </Group>
                    </Group>
                </FormSection>
            </ProfileMain>
        </ErrorBoundary>
    );
};
